// --- Consumption pattern ---
//
// In any hook:
//   const api = useApi();
//   try {
//     const { data, ok } = await api.fetchData(token, source, page, body);
//   } catch (err) {
//     // handle error — useApiAction re-throws on failure
//   }
//
// In any component — read status from store:
//   const isSaving  = useStore(s => s.actionStatus[ACTION_KEYS.SAVE_VIEW]?.status === "loading");
//   const saveError = useStore(s => s.actionStatus[ACTION_KEYS.SAVE_VIEW]?.error);
//   const isAnyLoading = useStore(s => s.isAnyLoading);
import { apiCore } from "../Api/apiCore";
import { ACTION_KEYS } from "../Utils/constants";
import { useApiAction } from "./useApiAction";
/**
 * Single facade over all apiCore functions.
 * Returns bound async functions — each automatically tracked in Zustand.
 *
 * Usage:
 *   const api = useApi();
 *   try {
 *     const { data, ok } = await api.fetchData(token, source, page, body);
 *   } catch (err) {
 *     // handle error — useApiAction re-throws on failure
 *   }
 */
export function useApi() {
    var runTokenStatus = useApiAction(ACTION_KEYS.TOKEN_STATUS);
    var runGetDataSource = useApiAction(ACTION_KEYS.GET_DATA_SOURCE);
    var runGetViews = useApiAction(ACTION_KEYS.GET_VIEWS);
    var runGetColumns = useApiAction(ACTION_KEYS.GET_COLUMNS);
    var runGetColumnCats = useApiAction(ACTION_KEYS.GET_COLUMN_CATS);
    var runExportCsv = useApiAction(ACTION_KEYS.EXPORT_CSV);
    var runExportJson = useApiAction(ACTION_KEYS.EXPORT_JSON);
    var runExportDataView = useApiAction(ACTION_KEYS.EXPORT_DATA_VIEW);
    var runUpdateView = useApiAction(ACTION_KEYS.UPDATE_VIEW);
    var runCloneView = useApiAction(ACTION_KEYS.CLONE_VIEW);
    var runDeleteView = useApiAction(ACTION_KEYS.DELETE_VIEW);
    var runFetchData = useApiAction(ACTION_KEYS.FETCH_DATA);
    var runFetchView = useApiAction(ACTION_KEYS.FETCH_VIEW);
    var runFetchDataView = useApiAction(ACTION_KEYS.FETCH_DATA_VIEW);
    var runSaveView = useApiAction(ACTION_KEYS.SAVE_VIEW);
    var runGetPresets = useApiAction(ACTION_KEYS.GET_PRESETS);
    var runGetDataViews = useApiAction(ACTION_KEYS.GET_DATA_VIEWS);
    return {
        tokenStatus: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runTokenStatus(function () { return apiCore.tokenStatus.apply(apiCore, args); });
        },
        getDataSource: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runGetDataSource(function () { return apiCore.getDataSource.apply(apiCore, args); });
        },
        getViews: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runGetViews(function () { return apiCore.getViews.apply(apiCore, args); });
        },
        getColumns: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runGetColumns(function () { return apiCore.getColumns.apply(apiCore, args); });
        },
        getColumnCategories: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runGetColumnCats(function () { return apiCore.getColumnCategories.apply(apiCore, args); });
        },
        exportCsv: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runExportCsv(function () { return apiCore.exportCsv.apply(apiCore, args); });
        },
        exportJson: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runExportJson(function () { return apiCore.exportJson.apply(apiCore, args); });
        },
        exportDataView: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runExportDataView(function () { return apiCore.exportDataView.apply(apiCore, args); });
        },
        updateView: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runUpdateView(function () { return apiCore.updateView.apply(apiCore, args); });
        },
        cloneView: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runCloneView(function () { return apiCore.cloneView.apply(apiCore, args); });
        },
        deleteView: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runDeleteView(function () { return apiCore.deleteView.apply(apiCore, args); });
        },
        fetchData: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runFetchData(function () { return apiCore.fetchData.apply(apiCore, args); });
        },
        fetchView: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runFetchView(function () { return apiCore.fetchView.apply(apiCore, args); });
        },
        fetchDataView: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runFetchDataView(function () { return apiCore.fetchDataView.apply(apiCore, args); });
        },
        saveView: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runSaveView(function () { return apiCore.saveView.apply(apiCore, args); });
        },
        getPresets: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runGetPresets(function () { return apiCore.getPresets.apply(apiCore, args); });
        },
        getDataViews: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return runGetDataViews(function () { return apiCore.getDataViews.apply(apiCore, args); });
        },
    };
}
